import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn.js';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, hint, id: providedId, label, rows = 3, ...props }, ref) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
    return (
      <div className="space-y-2">
        <label htmlFor={id} className="block text-xs font-semibold text-content/70">
          {label}
        </label>
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          aria-invalid={Boolean(error)}
          aria-describedby={descriptionId}
          className={cn(
            'hue-input w-full resize-y rounded-xl border bg-surface-elevated px-4 py-3 text-sm leading-6 text-content outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20',
            error ? 'border-danger/70' : 'border-surface-border',
            className,
          )}
          {...props}
        />
        {error ? (
          <p id={descriptionId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={descriptionId} className="text-xs leading-5 text-content-muted">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
